const krinkelsLogo = '/Krinkels-logo.png';
const husqvarnaLogo = '/Huqvarna-Logo.png';
const kressLogo = '/Kress-Logo.png';

import { auth, db } from '../firebase';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Preferences } from '@capacitor/preferences';

// React is used for JSX; keep imported to satisfy older tooling without unused-vars noise

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppAuthContext from '../hooks/useAppAuthContext';

import leafTexture from '../assets/Krinkels_Bladnerf_RGB.jpg';

import BiometricLogin from './BiometricLogin';

import InlineButtonLoader from './InlineButtonLoader';
import belroboticsLogo from '../assets/belrobotics-logo.png';
import '../styles/LoginPage.css';

const Login = () => {
  const [success, setSuccess] = useState('');
  const [approved, setApproved] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const BIOMETRIC_PREFS_KEY = 'biometric_auth_enabled';
  const BIOMETRIC_EMAIL_KEY = 'biometric_email';
  const BIOMETRIC_PASSWORD_KEY = 'biometric_password';

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);

      // Keep biometric auto-login credentials in sync after successful password login.
      try {
        const { value: biometricEnabled } = await Preferences.get({ key: BIOMETRIC_PREFS_KEY });
        if (biometricEnabled === 'true') {
          await Preferences.set({ key: BIOMETRIC_EMAIL_KEY, value: email });
          await Preferences.set({ key: BIOMETRIC_PASSWORD_KEY, value: password });
        } else {
          await Preferences.remove({ key: BIOMETRIC_PASSWORD_KEY });
        }
      } catch (prefsErr) {
        console.warn('Could not sync biometric login credentials:', prefsErr);
      }

      setError('');
      try { navigate('/meldingen', { replace: true }); } catch { window.location.href = '/meldingen'; }
    } catch (err) {
      let msg = 'Inloggen mislukt. ';
      if (err && err.code) {
        msg += `Foutcode: ${err.code}. `;
      }
      if (err && err.message) {
        msg += err.message;
      }
      setError(msg);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async (biometricPayload) => {
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const biometricEmail = typeof biometricPayload === 'string'
        ? biometricPayload
        : String(biometricPayload?.email || '').trim();
      const biometricPassword = typeof biometricPayload === 'object'
        ? String(biometricPayload?.password || '')
        : '';

      if (!biometricEmail) {
        throw new Error('Geen biometrisch account gevonden');
      }

      setEmail(biometricEmail);

      if (biometricPassword) {
        await signInWithEmailAndPassword(auth, biometricEmail, biometricPassword);
        setSuccess('Biometrische verificatie geslaagd. Je bent automatisch ingelogd.');
        try { navigate('/meldingen', { replace: true }); } catch { window.location.href = '/meldingen'; }
        return;
      }

      setSuccess('Biometrische verificatie geslaagd. Vul eenmalig je wachtwoord in om automatisch inloggen te activeren.');
      setPassword('');
      setTimeout(() => {
        document.getElementById('password')?.focus?.();
      }, 200);
    } catch (err) {
      console.error('Biometric login error:', err);
      setError('Biometrische inloggen mislukt: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Wachtwoordreset-e-mail verzonden. Controleer je inbox.');
      setEmail('');
      setIsResetting(false);
    } catch (err) {
      setError('Fout bij wachtwoord reset: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const { user } = useAppAuthContext();

  const checkApprovalStatus = async () => {
    if (!user) return;
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      setApproved(userSnap.data().approved);
    }
  };

  useEffect(() => {
    checkApprovalStatus();
  }, [user]);

  const navigate = useNavigate();
  useEffect(() => {
    if (user) {
      try { navigate('/meldingen', { replace: true }); } catch { void 0; }
    }
  }, [user, navigate]);

  return (
    <div className="loginv2-page">
      <div className="loginv2-shell">
        <header className="loginv2-topbar">
          <a className="loginv2-main-brand" href="/" aria-label="Terug naar de RBH-homepage">
            <img src={krinkelsLogo} alt="Krinkels" className="loginv2-primary-logo" />
          </a>
          <span className="loginv2-topbar-context">Robotbeheer Krinkels · Interne applicatie</span>
        </header>

        <main className="loginv2-main">
          <aside className="loginv2-intro" aria-labelledby="loginv2-intro-title">
            <img src={leafTexture} alt="" aria-hidden="true" className="loginv2-leaf" />
            <div className="loginv2-intro-content">
              <p className="loginv2-kicker">ÉÉN OVERZICHT. ALLE ROBOTS.</p>
              <h1 className="loginv2-title" id="loginv2-intro-title">
                Robotbeheer,<span> duidelijk geregeld.</span>
              </h1>
              <p className="loginv2-subtitle">
                De interne omgeving van Krinkels voor meldingen, robotdossiers,
                tickets en inzicht per vestiging.
              </p>
              <div className="loginv2-capabilities" aria-label="Onderwerpen in RBH">
                <div className="loginv2-capability"><span className="loginv2-capability-mark" aria-hidden="true" />Meldingen en tickets</div>
                <div className="loginv2-capability"><span className="loginv2-capability-mark" aria-hidden="true" />Robotdossiers en onderhoud</div>
                <div className="loginv2-capability"><span className="loginv2-capability-mark" aria-hidden="true" />Vestigingsgericht inzicht</div>
              </div>
            </div>
          </aside>

          <section className="loginv2-card" aria-labelledby="loginv2-auth-title">
            <header className="loginv2-header">
              <span className="loginv2-form-kicker">BEVEILIGDE MEDEWERKERSOMGEVING</span>
              <h2 className="loginv2-auth-title" id="loginv2-auth-title">
                {isResetting ? 'Wachtwoord herstellen' : 'Welkom terug'}
              </h2>
              <p className="loginv2-auth-subtitle">
                {isResetting
                  ? 'Vul je zakelijke e-mailadres in. Je ontvangt een link om je wachtwoord opnieuw in te stellen.'
                  : 'Log in met het e-mailadres en wachtwoord van je RBH-account.'}
              </p>
            </header>

            {error && <div role="alert" className="loginv2-alert loginv2-alert-error">{error}</div>}
            {success && <div role="status" className="loginv2-alert loginv2-alert-success">{success}</div>}
            {approved === false && <div role="alert" className="loginv2-alert loginv2-alert-error">Je account is nog niet goedgekeurd door een beheerder.</div>}

            {isResetting ? (
              <form className="loginv2-form" onSubmit={handlePasswordReset}>
                <div className="loginv2-field">
                  <label htmlFor="email">E-mailadres</label>
                  <input id="email" name="email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                    className="loginv2-input" placeholder="naam@krinkels.nl" />
                </div>
                <button type="submit" className="loginv2-btn loginv2-btn-primary" disabled={loading}>
                  <span>{loading ? <InlineButtonLoader label="Verzenden..." /> : 'Resetlink versturen'}</span>
                  <span className="loginv2-arrow" aria-hidden="true">→</span>
                </button>
                <button type="button" className="loginv2-btn-link"
                  onClick={() => { setIsResetting(false); setError(''); }} disabled={loading}>
                  Terug naar inloggen
                </button>
              </form>
            ) : (
              <form className="loginv2-form" onSubmit={handleLogin}>
                <div className="loginv2-field">
                  <label htmlFor="email">E-mailadres</label>
                  <input id="email" name="email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                    className="loginv2-input" placeholder="naam@krinkels.nl" />
                </div>
                <div className="loginv2-field">
                  <div className="loginv2-label-row">
                    <label htmlFor="password">Wachtwoord</label>
                    <button type="button" className="loginv2-inline-link"
                      onClick={() => { setIsResetting(true); setError(''); setSuccess(''); }}
                      disabled={loading}>Vergeten?</button>
                  </div>
                  <div className="loginv2-password-row">
                    <input id="password" name="password" type={showPassword ? 'text' : 'password'}
                      value={password} onChange={(e) => setPassword(e.target.value)} required
                      autoComplete="current-password" className="loginv2-input"
                      placeholder="Vul je wachtwoord in" />
                    <button type="button" className="loginv2-toggle-password"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
                      aria-pressed={showPassword}>
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M3 3l18 18M10.6 5.1A11 11 0 0 1 12 5c7 0 11 7 11 7a18.4 18.4 0 0 1-4 4.7M6 6.2C2.8 8.3 1 12 1 12s4 7 11 7c1.7 0 3.2-.4 4.5-1.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <button type="submit" className="loginv2-btn loginv2-btn-primary" disabled={loading}>
                  <span>{loading ? <InlineButtonLoader label="Inloggen..." /> : 'Inloggen bij RBH'}</span>
                  <span className="loginv2-arrow" aria-hidden="true">→</span>
                </button>

                <BiometricLogin onBiometricLogin={handleBiometricLogin} disabled={loading} />

                <div className="loginv2-divider" aria-hidden="true"><span>ANDERE AANMELDMETHODE</span></div>
                <div className="loginv2-microsoft" aria-label="Microsoft-aanmelding niet beschikbaar">
                  <button type="button" className="loginv2-btn loginv2-microsoft-btn" disabled
                    aria-describedby="loginv2-microsoft-notice">
                    <span className="loginv2-microsoft-mark" aria-hidden="true">
                      <span /><span /><span /><span />
                    </span>
                    Inloggen met Microsoft
                  </button>
                  <p id="loginv2-microsoft-notice" className="loginv2-microsoft-notice">
                    Microsoft-login is op dit moment niet beschikbaar. Contacteer RBH Support voor een account.
                  </p>
                </div>
              </form>
            )}
          </section>
        </main>

        <footer className="loginv2-footer">
          <div className="loginv2-footer-info">
            <span>&copy; {new Date().getFullYear()} Krinkels · Robotbeheer</span>
            <a href="/.well-known/security.txt" target="_blank" rel="noopener noreferrer">Security contact</a>
          </div>
          <div className="loginv2-partners" aria-label="Robotmerken">
            <img src={belroboticsLogo} alt="Belrobotics" />
            <img src={husqvarnaLogo} alt="Husqvarna" />
            <img src={kressLogo} alt="Kress" />
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Login;
